import { ref, computed, inject, isRef, unref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { RequestService } from '../services/RequestService'
import { useEmployerStore } from './EmployerStore'
import { isEmpty, random, filter, get, toString } from 'lodash'
import { calculateGroupInsuranceTypeId } from '../utils'
import { useSystemParamsStore } from './SystemParamsStore'
import { showQuestionCore, findOrphanConditionalAnswers } from '../utils/questionsAndAnswersHelpers'
import * as Sentry from '@sentry/vue'
import axios from 'axios'

const { simpleGet, post, put, handleErrors } = RequestService()

export const useQuestionsStore = defineStore(
  'questions',
  () => {
    const employerStore = useEmployerStore()
    const functionsBaseUrl = inject('functionsBaseUrl')
    const assessmentQuestions = ref([])
    const planQuestions = ref([])
    const planningQuestions = ref([])
    const isLoading = ref(true)

    const lastPlanningSectionGroup = ref('')

    const assessKeys = ref([])
    const mainAssessKey = ref('')
    const erisaDate = ref('')

    const planningKeys = ref([])
    const mainPlaningKey = ref('')

    const previousActionItemIds = ref([])
    const lastSavedActionItemIds = ref([])

    const actionItemsOfAssessment = computed(() => {
      let actionItems = []
      Object.values(assessmentQuestions.value).map((questions) => {
        questions.map((question) => {
          if (question.action_item && question.wrongAnswer && showQuestion(question).show) {
            const wrongAnswer = get(question, 'wrongAnswer.values', [])
            let titles = null
            let bodies = null
            let notes = []
            let responsibleParty = []
            let isActionItemArray = false
            try {
              titles = JSON.parse(question.action_item_title)
              bodies = JSON.parse(question.action_item)
              isActionItemArray = Array.isArray(titles) && Array.isArray(bodies)
              if (isActionItemArray && question.action_items_notes) {
                notes = JSON.parse(question.action_items_notes)
              }
              if (isActionItemArray && question.action_items_resp_party) {
                responsibleParty = JSON.parse(question.action_items_resp_party)
              }
            } catch (e) {
              if (isActionItemArray) {
                responsibleParty = []
              } else {
                isActionItemArray = false
              }
            }
            const hasWrongAnswer = Array.isArray(question.answer)
              ? question.answer.some((answer) => wrongAnswer.includes(answer))
              : wrongAnswer.includes(question.answer)

            if (hasWrongAnswer) {
              if (isActionItemArray) {
                const answerArr = Array.isArray(question.answer) ? question.answer : [question.answer]
                wrongAnswer.forEach((wrongVal, index) => {
                  if (answerArr.includes(wrongVal) && index < titles.length) {
                    actionItems.push({
                      title: titles[index],
                      body: bodies[index],
                      originalQuestion: question,
                      action_items_notes: notes[index],
                      arrayIndex: index,
                      responsibleParty: Array.isArray(responsibleParty[index]) ? responsibleParty[index] : [],
                      key: random(1, 1000, true)
                    })
                  }
                })
              } else {
                actionItems.push({
                  title: question.action_item_title,
                  body: question.action_item,
                  originalQuestion: question,
                  action_items_notes: question.action_items_notes,
                  responsibleParty: question.action_items_resp_party,
                  key: random(1, 1000, true)
                })
              }
            }
          }
        })
      })
      return actionItems
    })

    let allAnswers = []
    let existingId = []

    const originalQuestions = ref([])

    function getAnswerById(id) {
      return allAnswers[id]
    }

    // Live (reactive) answer of a question; allAnswers is only refreshed on save.
    function getLiveAnswerById(id) {
      const question = originalQuestions.value.find((q) => String(q.id) === String(id))
      if (question && question.answer !== undefined) {
        return isRef(question.answer) ? unref(question.answer) : question.answer
      }
      return allAnswers[id]
    }

    // Resets every assessment answer that points to a conditional option no longer visible
    // (e.g. "N/A" after Q2 left "Not currently..."). Returns the reset question ids.
    function clearOrphanConditionalAnswers() {
      const orphans = findOrphanConditionalAnswers(assessmentQuestions.value, getLiveAnswerById)
      return orphans.map(({ question, optionIndexes }) => {
        const current = isRef(question.answer) ? unref(question.answer) : question.answer
        if (Array.isArray(current)) {
          question.answer = current.filter((idx) => !optionIndexes.includes(idx))
          allAnswers[question.id] = [...question.answer]
        } else {
          question.answer = ''
          allAnswers[question.id] = ''
        }
        return question.id
      })
    }

    function normalizeAnswer(answer) {
      if (answer && answer.length > 0) {
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(answer)) {
          return answer
        }

        if (isNaN(answer)) {
          //its an array
          let returnValue = null
          try {
            returnValue = JSON.parse(answer)
          } catch (error) {
            Sentry.captureException(new Error(answer, error))
            returnValue = answer
          }
          return returnValue
        } else {
          return parseInt(answer)
        }
      }
    }

    function getCompletionStatus(section) {
      let updatedQuestions = assessmentQuestions.value[section].filter((val) => {
        if (val.hideLogic !== null) {
          return showQuestion(val).show
        }
        return true
      })
      let unansweredQuestions = updatedQuestions.filter((val) => {
        let obj = val.answer
        if (obj || obj === 0) {
          switch (typeof obj) {
            case 'number':
              return false
            case 'object':
            case 'string':
              return !(obj.length > 0)
          }
        } else {
          return true
        }
        return (
          obj !== null &&
          (typeof obj?.value === 'number' ||
            ((typeof obj?.value === 'string' || typeof obj?.value === 'object') &&
              obj?.value.length > 0))
        )
      })
      let status = ''
      if (unansweredQuestions.length === 0) {
        status = 'filled'
      }
      if (unansweredQuestions.length > 0) {
        status = 'partial'
      }
      if (unansweredQuestions.length === updatedQuestions.length) {
        status = 'empty'
      }
      return status
    }

    function showQuestion(question) {
      return showQuestionCore(question, getAnswerById)
    }

    function clearQuestions(questionType) {
      const questionsToProcess =
        questionType === 'plan' ? planQuestions.value
        : questionType === 'assessment' ? assessmentQuestions.value
        : planningQuestions.value
      //we run a for and clear all values
      if (questionType === 'assessment') {
        erisaDate.value = ''
      }
      Object.values(questionsToProcess).forEach((section) => {
        section.forEach((element) => {
          if (element.type == 1){
            element.answer = ref([])
            allAnswers[element.id] = []
          } else {
            element.answer = ref('')
            allAnswers[element.id] = ''
          }
        })
      })
    }

    function isQuestionsComplete(questions) {
      let pendingQuestions = questions.filter((val) => {
        if (val.answer !== undefined) {
          if (typeof val.answer === 'object') {
            return Object.keys(val.answer).length === 0
          }
          return val.answer === '' || val.answer === null || val.answer === undefined
        } else {
          return true
        }
      })
      return pendingQuestions.length > 0 ? false : true
    }

    function isPlanComplete() {
      if (!planQuestions.value || Object.keys(planQuestions.value).length === 0) {
        return false
      }
      const section = Object.values(planQuestions.value)[0] ?? []
      if (section.length === 0) {
        return false
      }
      const requiredPlanQuestions = section.filter((question) => !question.isOptional)
      return isQuestionsComplete(requiredPlanQuestions)
    }

    function isAssessmentComplete() {
      if (!assessmentQuestions.value || Object.keys(assessmentQuestions.value).length === 0) {
        return false
      }
      const finalAssessmentQuestions = []
      Object.values(assessmentQuestions.value).forEach((section) => {
        section.forEach((question) => {
          if (showQuestion(question).show) {
            finalAssessmentQuestions.push(question)
          }
        })
      })
      return isQuestionsComplete(finalAssessmentQuestions)
    }

    function isAssessmentStarted() {
      const sections = Object.values(assessmentQuestions.value)
      if (sections.length === 0) {
        return false
      }
      return sections.some((questions) =>
        questions.some(
          (question) =>
            showQuestion(question).show &&
            ((Array.isArray(question.answer) && question.answer.length > 0) ||
              (typeof question.answer === 'string' && question.answer !== ''))
        )
      )
    }

    function isPlanningStarted() {
      if (planningQuestions.value.length === 0) {
        return false
      }
      let response = false
      loop1: for (let section in planningQuestions.value) {
        for (let question of planningQuestions.value[section]) {
          if (showQuestion(question).show) {
            if (Array.isArray(question.answer) && question.answer.length > 0) {
              response = true
              break loop1
            } else if (typeof question.answer === 'string' && question.answer !== '') {
              response = true
              break loop1
            }
          }
        }
      }
      return response
    }

    function parseScalarAnswer(answer) {
      if (answer === null || answer === undefined || answer === '') {
        return undefined
      }

      try {
        return JSON.parse(answer)
      } catch (error) {
        return answer
      }
    }

    async function loadQuestionsAndAnswers(planYearId = null) {
      isLoading.value = true
      try {
        const { actualPlanYearId } = storeToRefs(employerStore)

        let endpoints = [
          `${functionsBaseUrl}/questionsGateway/api/v1/plan-years/${planYearId || actualPlanYearId.value}/questions`,
        ]
        await axios.all(endpoints.map((promise) => simpleGet('loadQuestionsAndAnswers', promise))).then(
          axios.spread((inQuestions) => {
            for (let i = 0; i < inQuestions.data.data.length; i++) {
              let element = inQuestions.data.data[i]
              if (!existingId.includes(element.id)) {
                element.options = element.options.values
                allAnswers[element.id] = normalizeAnswer(element.answer)
                switch (element.type) {
                  case 0:
                    element.answer = element.answer ? ref(JSON.parse(element.answer)) : ref('')
                    break
                  case 1:
                    element.answer = element.answer ? ref(JSON.parse(element.answer)) : ref([])
                    break
                  case 2:
                  case 3:
                    element.answer = element.answer ? ref(parseScalarAnswer(element.answer)) : ref()
                    break
                  case 4:
                    if (
                      element.answer !== '1' &&
                      element.answer !== '2' &&
                      !isEmpty(element.answer)
                    ) {
                      erisaDate.value = element.answer
                      element.answer = 0
                    } else if (isEmpty(element.answer)) {
                      erisaDate.value = ''
                      element.answer = ''
                    } else {
                      element.answer = parseInt(element.answer, 10)
                    }
                    break
                }
                //we review the action_items_resp_party field
                element.action_items_resp_party =
                  element.action_items_resp_party && element.action_items_resp_party.length > 0 ?
                    JSON.parse(element.action_items_resp_party)
                  : ref([false, false, false])

                originalQuestions.value.push(element)
                existingId.push(element.id)
              }
            }

            planQuestions.value = groupBy(
              filter(originalQuestions.value, (val) => val.section_id === '10'),
              'title'
            )
            const ASSESSMENT_SECTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '11', '12']
            assessmentQuestions.value = groupBy(
              filter(originalQuestions.value, (val) => ASSESSMENT_SECTIONS.includes(val.section_id)),
              'title'
            )
            assessKeys.value = Object.keys(assessmentQuestions.value)
            mainAssessKey.value = Object.keys(planQuestions.value)[0]

            // Initialize lastSavedActionItemIds from loaded answers
            const initialActionItemIds = []
            Object.values(assessmentQuestions.value).forEach((section) => {
              section.forEach((element) => {
                if (element.wrongAnswer && showQuestion(element).show) {
                  const wrongValues = get(element, 'wrongAnswer.values', [])
                  if (wrongValues.length > 0) {
                    const answerVal = allAnswers[element.id]
                    const hasWrongAnswer = Array.isArray(answerVal)
                      ? answerVal.some(v => wrongValues.includes(v))
                      : wrongValues.includes(answerVal)
                    if (hasWrongAnswer) {
                      initialActionItemIds.push(element.id)
                    }
                  }
                }
              })
            })
            lastSavedActionItemIds.value = initialActionItemIds
            previousActionItemIds.value = [...initialActionItemIds]

            //We initialize the planning Guide questions

            let finalSectionGroup = getPlanningSectionGroup() // 'SMALL GROUP', 'SMALL GROUP - SELF INSURED', 'LARGE GROUPS', 'LARGE GROUP - SELF INSURED',
            if (finalSectionGroup !== '') {
              planningQuestions.value = groupBy(
                filter(originalQuestions.value, (val) => {
                  return (
                    val.section_group === finalSectionGroup &&
                    val.section_type === 0 &&
                    val.is_main === 0
                  )
                }),
                'title'
              )
              planningKeys.value = Object.keys(planningQuestions.value).sort((a, b) => {
                if (a === 'Onboarding') return -1
                if (b === 'Onboarding') return 1
                return 0
              })
              mainPlaningKey.value = Object.keys(planningQuestions.value)[0]
            }
          })
        )
      } catch (error) {
        handleErrors('Error loading questions:', error)
      } finally {
        isLoading.value = false
      }
    }

    function getAnswersByQuestionIds(questionIds) {
      const answersMap = {}
      questionIds.forEach(qId => {
        answersMap[String(qId)] = allAnswers[qId] ?? null
      })
      return answersMap
    }

    async function saveQuestions(questionType, noteId = null) {
      try {
        const { actualEmployer, actualPlanYear } = storeToRefs(employerStore)

        const questionsToProcess =
          questionType === 'plan' ? planQuestions.value
          : questionType === 'assessment' ? assessmentQuestions.value
          : planningQuestions.value

        if (questionType === 'assessment') {
          previousActionItemIds.value = [...lastSavedActionItemIds.value]
        }

        const responsesToStore = []
        const actionItemsQuestionIds = []

        Object.values(questionsToProcess).forEach((object) => {
          const innerQuestions = object.map((element) => {
            // Unwrap ref when storing answer value
            const rawAnswer = isRef(element.answer) ? unref(element.answer) : element.answer
            allAnswers[element.id] = Array.isArray(rawAnswer) ? [...rawAnswer] : rawAnswer
            
            // Check if this answer triggers an action item
            if (element.wrongAnswer) {
              let wrongValues = []
              try {
                const parsedWrong = typeof element.wrongAnswer === 'string'
                  ? JSON.parse(element.wrongAnswer)
                  : element.wrongAnswer
                wrongValues = Array.isArray(parsedWrong?.values)
                  ? parsedWrong.values
                  : Array.isArray(parsedWrong)
                    ? parsedWrong
                    : []
              } catch (e) {
                console.warn(`Failed to parse wrongAnswer for question ${element.id}:`, e)
              }

              if (wrongValues.length > 0) {
                // Parse the answer to compare
                let answerVal = allAnswers[element.id]
                if (element.type !== 4 && typeof answerVal === 'string') {
                  try {
                    answerVal = JSON.parse(answerVal)
                  } catch (e) {
                    // Keep original value if parsing fails
                  }
                }

                // Check if answer matches any wrong value
                const hasWrongAnswer = Array.isArray(answerVal)
                  ? answerVal.some(v => wrongValues.includes(v))
                  : wrongValues.includes(answerVal)

                if (hasWrongAnswer && showQuestion(element).show) {
                  if (!element.action_item) {
                    handleErrors("Load action tiems", 
                                new Error(`Business Error: Question ${element.id} has wrong answer options but no action_item defined!!`))
                  }
                  actionItemsQuestionIds.push(element.id)
                }
              }
            }
            
            const isNoteEdited = noteId === element.id

            // Update local element for immediate UI display (e.g. "Today")
            if (isNoteEdited) {
              element.note_updated_at = new Date().toISOString()
            }

            if (element.type === 4) {
              return {
                answer_id: element.answer_id,
                question_id: element.id,
                employer_plan_years_id: actualPlanYear.value.id,
                answer: element.answer === 0 ? erisaDate.value : toString(element.answer),
                employerId: actualEmployer.value.id,
                notes: element.notes || '',
                note_edited: isNoteEdited,
              }
            }
            const result = {
              answer_id: element.answer_id,
              question_id: element.id,
              employer_plan_years_id: actualPlanYear.value.id,
              answer: JSON.stringify(element.answer),
              employerId: actualEmployer.value.id,
              notes: element.notes,
              note_edited: isNoteEdited,
              action_items_notes: element.action_items_notes,
              action_items_resp_party: JSON.stringify(element.action_items_resp_party),
              action_items_note_edited: !!element.action_items_note_edited,
            }
            // Reset flag so subsequent saves don't re-trigger backend timestamp for notes
            element.action_items_note_edited = false
            return result
          })
          responsesToStore.push(...innerQuestions)
        })

        let objectToPut = null
        if (questionType === 'plan' || questionType === 'planning') {
          actualEmployer.value['plan_start_date'] = actualPlanYear.value.plan_start_date
          actualEmployer.value['complianceYearId'] = actualPlanYear.value.id
          const planComplete = isPlanComplete()
          actualEmployer.value['is_plan_complete'] = planComplete
          actualEmployer.value['is_assessment_complete'] = isAssessmentComplete()
          // Recalculate groupInsuranceType based on current answers 9 and 11 only if plan is complete
          const employeesOnPlan = getAnswerById(9)
          const fundingTypeAns = getAnswerById(11)
          const { largeGroupCutoff } = storeToRefs(useSystemParamsStore())
          const groupInsuranceType = planComplete
            ? calculateGroupInsuranceTypeId(employeesOnPlan, fundingTypeAns, largeGroupCutoff.value)
            : null
          actualPlanYear.value['groupInsuranceType'] = groupInsuranceType
          objectToPut = { 
            id: actualEmployer.value.id, 
            ...actualEmployer.value, 
            groupInsuranceType
          }
        } else if (questionType === 'assessment') {
          // Update employer with action items for assessment questions
          objectToPut = { 
            id: actualEmployer.value.id, 
            ...actualEmployer.value,
            is_assessment_complete: isAssessmentComplete(),
            groupInsuranceType: actualPlanYear.value.groupInsuranceType,
            complianceYearId: actualPlanYear.value.id,
            action_items_question_ids: actionItemsQuestionIds
          }
        }
        if (objectToPut) {
          await put('updateEmployer', 
              `${functionsBaseUrl}/employerGateway/api/v1/employers/${actualEmployer.value.id}`, 
              objectToPut)
        }
        if (questionType === 'assessment') {
          lastSavedActionItemIds.value = [...actionItemsQuestionIds]
        }

        // Bulk save via Firestore batch function
        
        await post('saveAnswers', 
          `${functionsBaseUrl}/questionsGateway/api/v1/answers`,
           responsesToStore)
          
      } catch (error) {
        handleErrors('Error saving questions:', error)
      }
    }

    function getPlanningSectionGroup() {
      let insuranceType = getAnswerById(11)
      let noEmployees = getAnswerById(9)

      if (typeof insuranceType === 'object') {
        insuranceType = insuranceType.value
      }
      if (typeof noEmployees === 'object') {
        noEmployees = noEmployees.value
      }
      const { largeGroupCutoff } = storeToRefs(useSystemParamsStore())
      let finalSectionGroup = ''
      if (
        noEmployees &&
        (insuranceType != '' || insuranceType != null || insuranceType != undefined)
      ) {
        let insuranceTypes = ['FI', 'SI', 'LF', 'DK'] // Fully insured // Self insured // Level funded // Don't know
        let currentInsurance = insuranceTypes[insuranceType]
        const isLargeGroup = noEmployees >= largeGroupCutoff.value
        if (!isLargeGroup && currentInsurance === 'FI') {
          finalSectionGroup = 'SMALL GROUP'
        } else if (isLargeGroup && currentInsurance === 'FI') {
          finalSectionGroup = 'LARGE GROUPS'
        } else if (!isLargeGroup && currentInsurance !== 'FI') {
          finalSectionGroup = 'SMALL GROUP - SELF INSURED'
        } else if (isLargeGroup && currentInsurance !== 'FI') {
          finalSectionGroup = 'LARGE GROUP - SELF INSURED'
        }
      }
      if (lastPlanningSectionGroup.value == '') {
        lastPlanningSectionGroup.value = finalSectionGroup
      }
      return finalSectionGroup
    }

    function changeLastPlanningSectionGroup() {
      lastPlanningSectionGroup.value = getPlanningSectionGroup()
    }

    function hideDueICHRA(key) {
      if (key === 'ICHRA') {
        let initialOptions = getAnswerById(12)
        if (typeof initialOptions === 'object' && initialOptions.value) {
          initialOptions = JSON.parse(initialOptions.value)
        }
        return !initialOptions?.includes(10)
      }
      return false
    }

    function isValidDate(date) {
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/
      return dateRegex.test(date)
    }

    function updateAssessmentQuestion(questionToUpdate) {
      // Mutate in place. A spread clone would break the shared reference with
      // originalQuestions; loadQuestionsAndAnswers then regroups from the stale original.
      Object.keys(assessmentQuestions.value).forEach((category) => {
        assessmentQuestions.value[category] = assessmentQuestions.value[category].map((question) => {
          if (question.id === questionToUpdate.id) {
            question.action_items_notes = questionToUpdate.action_items_notes
            question.action_items_resp_party = questionToUpdate.action_items_resp_party
          }
          return question
        })
      })
    }
    function resetQuestionStore() {
      isLoading.value = true
      assessmentQuestions.value = []
      planQuestions.value = []
      planningQuestions.value = []
      assessKeys.value = []
      mainAssessKey.value = ''
      planningKeys.value = []
      mainPlaningKey.value = ''
      originalQuestions.value = []
      previousActionItemIds.value = []
      lastSavedActionItemIds.value = []
      lastPlanningSectionGroup.value = ''
      allAnswers = []
      existingId = []
    }
    //aux functions
    const groupBy = (xs, key) => {
      return xs.reduce(function (rv, x) {
        ;(rv[x[key]] = rv[x[key]] || []).push(x)
        return rv
      }, {})
    }

    return {
      allAnswers,

      planQuestions,

      assessmentQuestions,
      mainAssessKey,
      assessKeys,

      erisaDate,

      planningQuestions,
      mainPlaningKey,
      planningKeys,
      isLoading,
      actionItemsOfAssessment,
      originalQuestions,
      lastPlanningSectionGroup,
      resetQuestionStore,
      loadQuestionsAndAnswers,
      saveQuestions,
      clearQuestions,
      showQuestion,
      getPlanningSectionGroup,
      changeLastPlanningSectionGroup,
      getCompletionStatus,
      isPlanComplete,
      isAssessmentComplete,
      isAssessmentStarted,
      isPlanningStarted,
      getAnswerById,
      getAnswersByQuestionIds,
      getLiveAnswerById,
      clearOrphanConditionalAnswers,
      hideDueICHRA,
      updateAssessmentQuestion,
      isValidDate,
      previousActionItemIds,
      lastSavedActionItemIds
    }
  },
  {
    persist: true
  }
)
